import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useAuth } from '../hooks/useAuth';
import empleadoService from '../services/empleadoService';
import planillaService from '../services/planillaService';
import planillaCierreService from '../services/planillaCierreService';
import configuracionContratosService from '../services/configuracionContratosService';
import contratosEmpleadoService from '../services/contratosEmpleadoService';
import configuracionAniversarioLaboralService from '../services/configuracionAniversarioLaboralService';
import configuracionExpedienteLaboralService from '../services/configuracionExpedienteLaboralService';
import expedienteLaboralService from '../services/expedienteLaboralService';
import configuracionSsomaService from '../services/configuracionSsomaService';
import ssomaService from '../services/ssomaService';
import { pathWithEmpresa } from '../utils/companyUrl';
import { useEmpresaPath } from '../hooks/useEmpresaPath';
import { useNavigation } from '../hooks/useNavigation';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatearFecha = (fecha) => {
  if (!fecha) return '';
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  if (!y || !m || !d) return texto;
  return `${d}/${m}/${y}`;
};

const ALERTAS_CONTRATOS_VACIAS = {
  por_vencer: [],
  pendientes: [],
  vencidos: [],
  totales: { por_vencer: 0, pendientes: 0, vencidos: 0 },
};

const ALERTAS_EXPEDIENTE_VACIAS = {
  por_vencer: [],
  vencidos: [],
  totales: { por_vencer: 0, vencidos: 0, total: 0 },
  dias_alerta: 15,
};

const ALERTAS_SSOMA_VACIAS = {
  activo: false,
  sctr: { por_vencer: [], vencidos: [] },
  emo: { por_vencer: [], vencidos: [] },
  epp: { por_vencer: [], vencidos: [] },
  cursos: { por_vencer: [], vencidos: [] },
  accidentes_abiertos: [],
  totales: {
    sctr_por_vencer: 0,
    sctr_vencidos: 0,
    emo_por_vencer: 0,
    emo_vencidos: 0,
    epp_por_vencer: 0,
    epp_vencidos: 0,
    cursos_por_vencer: 0,
    cursos_vencidos: 0,
    accidentes_abiertos: 0,
  },
  dias_alerta: 15,
};

const Dashboard = () => {
  const { selectedCompany, empresaIdFromUrl } = useAuth();
  const { isSsoma } = useNavigation();
  const empresaActivaId = selectedCompany?.id || empresaIdFromUrl;
  const nuevoEmpleadoPath = useEmpresaPath('/empleados/nuevo');
  const [totalEmpleados, setTotalEmpleados] = useState(0);
  const [totalPlanillas, setTotalPlanillas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [ingresosPorMes, setIngresosPorMes] = useState([]);
  const [contratosActivados, setContratosActivados] = useState(false);
  const [alertasContratos, setAlertasContratos] = useState(ALERTAS_CONTRATOS_VACIAS);
  const [loadingAlertasContratos, setLoadingAlertasContratos] = useState(false);
  const [aniversariosActivados, setAniversariosActivados] = useState(false);
  const [alertasAniversarios, setAlertasAniversarios] = useState({
    anios: null,
    dias_anticipacion: null,
    total: 0,
    empleados: [],
  });
  const [loadingAlertasAniversarios, setLoadingAlertasAniversarios] = useState(false);
  const [expedienteActivado, setExpedienteActivado] = useState(false);
  const [alertasExpediente, setAlertasExpediente] = useState(ALERTAS_EXPEDIENTE_VACIAS);
  const [loadingAlertasExpediente, setLoadingAlertasExpediente] = useState(false);
  const [ssomaActivado, setSsomaActivado] = useState(false);
  const [fichasNuevas, setFichasNuevas] = useState(0);
  const [alertasSsoma, setAlertasSsoma] = useState(ALERTAS_SSOMA_VACIAS);
  const [loadingAlertasSsoma, setLoadingAlertasSsoma] = useState(false);

  useEffect(() => {
    let cancelado = false;

    const extraerTotalEmpleados = (empleadosResponse) => {
      if (empleadosResponse.total !== undefined) return empleadosResponse.total;
      if (empleadosResponse.data && empleadosResponse.data.total !== undefined) return empleadosResponse.data.total;
      if (empleadosResponse.success && empleadosResponse.data && empleadosResponse.data.total !== undefined) {
        return empleadosResponse.data.total;
      }
      if (empleadosResponse.data && empleadosResponse.data.data && Array.isArray(empleadosResponse.data.data)) {
        return empleadosResponse.data.data.length;
      }
      if (empleadosResponse.data && Array.isArray(empleadosResponse.data)) return empleadosResponse.data.length;
      if (Array.isArray(empleadosResponse)) return empleadosResponse.length;
      return 0;
    };

    const extraerListaPlanillas = (planillasResponse) => {
      if (planillasResponse.success && planillasResponse.data && Array.isArray(planillasResponse.data)) {
        return planillasResponse.data;
      }
      if (planillasResponse.data && Array.isArray(planillasResponse.data)) return planillasResponse.data;
      if (Array.isArray(planillasResponse)) return planillasResponse;
      return [];
    };

    const resetSinEmpresa = () => {
      setTotalEmpleados(0);
      setTotalPlanillas(0);
      setIngresosPorMes([]);
      setContratosActivados(false);
      setAlertasContratos(ALERTAS_CONTRATOS_VACIAS);
      setAniversariosActivados(false);
      setAlertasAniversarios({ anios: null, dias_anticipacion: null, total: 0, empleados: [] });
      setExpedienteActivado(false);
      setAlertasExpediente(ALERTAS_EXPEDIENTE_VACIAS);
      setSsomaActivado(false);
      setAlertasSsoma(ALERTAS_SSOMA_VACIAS);
      setFichasNuevas(0);
      setLoading(false);
    };

    const cargarAlertas = async () => {
      const cargarContratos = async () => {
        try {
          const configContratos = await configuracionContratosService.obtenerConfiguracion(empresaActivaId);
          if (cancelado) return;
          const contratosActivo = configContratos.success ? (configContratos.data?.activo ?? false) : false;
          setContratosActivados(contratosActivo);
          if (!contratosActivo) {
            setAlertasContratos(ALERTAS_CONTRATOS_VACIAS);
            return;
          }
          setLoadingAlertasContratos(true);
          const alertasResponse = await contratosEmpleadoService.obtenerAlertasDashboard(empresaActivaId);
          if (cancelado) return;
          setAlertasContratos(alertasResponse.success ? alertasResponse.data : ALERTAS_CONTRATOS_VACIAS);
        } catch (error) {
          if (!cancelado) {
            console.error('Error cargando alertas de contratos:', error);
            setContratosActivados(false);
            setAlertasContratos(ALERTAS_CONTRATOS_VACIAS);
          }
        } finally {
          if (!cancelado) setLoadingAlertasContratos(false);
        }
      };

      const cargarAniversarios = async () => {
        try {
          const configAniv = await configuracionAniversarioLaboralService.obtenerConfiguracion(empresaActivaId);
          if (cancelado) return;
          const anivActivo = configAniv.success ? (configAniv.data?.activo ?? false) : false;
          setAniversariosActivados(anivActivo);
          if (!anivActivo) {
            setAlertasAniversarios({ anios: null, dias_anticipacion: null, total: 0, empleados: [] });
            return;
          }
          setLoadingAlertasAniversarios(true);
          const anios = configAniv.data?.anios ?? 5;
          const dias = configAniv.data?.dias_anticipacion ?? 60;
          const anivResponse = await empleadoService.getProximosAniversarios({
            empresa_id: empresaActivaId,
            anios,
            dias_anticipacion: dias,
          });
          if (cancelado) return;
          if (anivResponse.success && anivResponse.data) {
            setAlertasAniversarios({
              anios: anivResponse.data.anios ?? anios,
              dias_anticipacion: anivResponse.data.dias_anticipacion ?? dias,
              total: anivResponse.data.total ?? 0,
              empleados: anivResponse.data.empleados || [],
            });
          } else {
            setAlertasAniversarios({ anios, dias_anticipacion: dias, total: 0, empleados: [] });
          }
        } catch (errorAniv) {
          if (!cancelado) {
            console.error('Error cargando alertas de aniversarios:', errorAniv);
            setAniversariosActivados(false);
            setAlertasAniversarios({ anios: null, dias_anticipacion: null, total: 0, empleados: [] });
          }
        } finally {
          if (!cancelado) setLoadingAlertasAniversarios(false);
        }
      };

      const cargarExpediente = async () => {
        try {
          const configExpediente = await configuracionExpedienteLaboralService.obtenerConfiguracion(empresaActivaId);
          if (cancelado) return;
          const expedienteActivo = configExpediente.success ? (configExpediente.data?.activo ?? false) : false;
          setExpedienteActivado(expedienteActivo);
          if (!expedienteActivo) {
            setAlertasExpediente(ALERTAS_EXPEDIENTE_VACIAS);
            return;
          }
          setLoadingAlertasExpediente(true);
          const alertasExp = await expedienteLaboralService.obtenerAlertasDashboard(empresaActivaId);
          if (cancelado) return;
          if (alertasExp.success && alertasExp.data) {
            setAlertasExpediente({
              por_vencer: alertasExp.data.por_vencer || [],
              vencidos: alertasExp.data.vencidos || [],
              totales: alertasExp.data.totales || ALERTAS_EXPEDIENTE_VACIAS.totales,
              dias_alerta: alertasExp.data.dias_alerta ?? 15,
            });
          } else {
            setAlertasExpediente(ALERTAS_EXPEDIENTE_VACIAS);
          }
        } catch (errorExpediente) {
          if (!cancelado) {
            console.error('Error cargando alertas de expediente laboral:', errorExpediente);
            setExpedienteActivado(false);
            setAlertasExpediente(ALERTAS_EXPEDIENTE_VACIAS);
          }
        } finally {
          if (!cancelado) setLoadingAlertasExpediente(false);
        }
      };

      const cargarSsoma = async () => {
        try {
          const configSsoma = await configuracionSsomaService.obtenerConfiguracion(empresaActivaId);
          if (cancelado) return;
          const ssomaActivo = configSsoma.success ? (configSsoma.data?.activo ?? false) : false;
          setSsomaActivado(ssomaActivo);
          if (!ssomaActivo) {
            setAlertasSsoma(ALERTAS_SSOMA_VACIAS);
            return;
          }
          setLoadingAlertasSsoma(true);
          const alertasSsomaResp = await ssomaService.alertasDashboard(empresaActivaId);
          if (cancelado) return;
          if (alertasSsomaResp.success && alertasSsomaResp.data) {
            setAlertasSsoma({ ...ALERTAS_SSOMA_VACIAS, ...alertasSsomaResp.data });
          } else {
            setAlertasSsoma(ALERTAS_SSOMA_VACIAS);
          }
          if (isSsoma) {
            const fichasResp = await ssomaService.fichas(empresaActivaId);
            if (!cancelado) {
              setFichasNuevas((fichasResp.data || []).filter((f) => f.nuevo).length);
            }
          }
        } catch (errorSsoma) {
          if (!cancelado) {
            console.error('Error cargando alertas SSOMA:', errorSsoma);
            setSsomaActivado(false);
            setAlertasSsoma(ALERTAS_SSOMA_VACIAS);
          }
        } finally {
          if (!cancelado) setLoadingAlertasSsoma(false);
        }
      };

      if (isSsoma) {
        await cargarSsoma();
        return;
      }
      await Promise.allSettled([cargarContratos(), cargarAniversarios(), cargarExpediente(), cargarSsoma()]);
    };

    const cargarPrincipal = async () => {
      try {
        const [empleadosResponse, planillasResponse] = await Promise.all([
          empleadoService.getAllEmpleados({
            empresa_id: empresaActivaId,
            estado: 'Activo',
            per_page: 1,
          }),
          planillaService.obtenerPlanillasPorEmpresa(empresaActivaId),
        ]);
        if (cancelado) return;

        const totalEmpleadosActivos = extraerTotalEmpleados(empleadosResponse);
        const listaPlanillas = extraerListaPlanillas(planillasResponse);
        const planillasCerradas = listaPlanillas.filter((planilla) => planilla.estado === 'cerrada');

        setTotalEmpleados(totalEmpleadosActivos);
        setTotalPlanillas(planillasCerradas.length);
        setLoading(false);

        const now = new Date();
        const ultimos6Meses = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          ultimos6Meses.push({
            periodo: `${MESES[d.getMonth()]} ${d.getFullYear()}`,
            year: d.getFullYear(),
            month: d.getMonth(),
            fecha: d.getTime(),
          });
        }
        const parseYearMonth = (fechaStr) => {
          if (!fechaStr) return null;
          const s = String(fechaStr);
          const match = s.match(/^(\d{4})-(\d{2})/);
          if (!match) return null;
          return { year: parseInt(match[1], 10), month: parseInt(match[2], 10) - 1 };
        };
        const key = (y, m) => `${y}-${m}`;
        const totalesPorMesPlanilla = {};
        const totalesPorMesRH = {};
        ultimos6Meses.forEach((m) => {
          totalesPorMesPlanilla[key(m.year, m.month)] = 0;
          totalesPorMesRH[key(m.year, m.month)] = 0;
        });

        const estadisticas = await Promise.all(
          planillasCerradas.map(async (p) => {
            const ym = parseYearMonth(p.fecha_inicio);
            if (!ym) return null;
            const k = key(ym.year, ym.month);
            if (totalesPorMesPlanilla[k] === undefined) return null;
            const esRH = p.modalidad_planilla === 'planilla_rh' ||
              (p.nombre && String(p.nombre).toUpperCase().includes('RH'));
            try {
              const res = await planillaCierreService.obtenerEstadisticasPlanillaCerrada(p.id);
              return { k, esRH, num: Number(res?.data?.total_ingresos ?? 0) };
            } catch (_) {
              return null;
            }
          })
        );
        if (cancelado) return;

        estadisticas.forEach((item) => {
          if (!item) return;
          if (item.esRH) {
            totalesPorMesRH[item.k] += item.num;
          } else {
            totalesPorMesPlanilla[item.k] += item.num;
          }
        });

        setIngresosPorMes(ultimos6Meses.map((m) => ({
          ...m,
          totalIngresos: totalesPorMesPlanilla[key(m.year, m.month)] ?? 0,
          totalIngresosRH: totalesPorMesRH[key(m.year, m.month)] ?? 0,
        })));
      } catch (error) {
        if (cancelado) return;
        console.error('❌ Error al obtener datos para dashboard:', error);
        setTotalEmpleados(0);
        setTotalPlanillas(0);
        setIngresosPorMes([]);
        setLoading(false);
      }
    };

    if (!empresaActivaId) {
      resetSinEmpresa();
      return undefined;
    }

    setLoading(true);
    if (isSsoma) {
      setLoading(false);
      cargarAlertas();
    } else {
      cargarPrincipal();
      cargarAlertas();
    }

    return () => {
      cancelado = true;
    };
  }, [empresaActivaId, selectedCompany?.name, isSsoma]);

  // Si no hay empresa seleccionada, mostrar mensaje
  if (!empresaActivaId) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver el panel principal.
          </p>
        </div>
      </div>
    );
  }

  if (isSsoma) {
    const ssomaPath = pathWithEmpresa('/ssoma', empresaActivaId);
    const kpis = [
      { titulo: 'SCTR vencidos', valor: alertasSsoma.totales?.sctr_vencidos, color: 'red' },
      { titulo: 'SCTR por vencer', valor: alertasSsoma.totales?.sctr_por_vencer, color: 'amber' },
      { titulo: 'EMO vencidos', valor: alertasSsoma.totales?.emo_vencidos, color: 'red' },
      { titulo: 'EMO por vencer', valor: alertasSsoma.totales?.emo_por_vencer, color: 'amber' },
      { titulo: 'EPP vencidos', valor: alertasSsoma.totales?.epp_vencidos, color: 'red' },
      { titulo: 'EPP por vencer', valor: alertasSsoma.totales?.epp_por_vencer, color: 'amber' },
      { titulo: 'Cursos vencidos', valor: alertasSsoma.totales?.cursos_vencidos, color: 'red' },
      { titulo: 'Cursos por vencer', valor: alertasSsoma.totales?.cursos_por_vencer, color: 'amber' },
      { titulo: 'Accidentes abiertos', valor: alertasSsoma.totales?.accidentes_abiertos, color: 'rose' },
      { titulo: 'Fichas nuevas', valor: fichasNuevas, color: 'emerald' },
    ];
    const bloques = [
      { titulo: 'SCTR vencidos', items: alertasSsoma.sctr?.vencidos, tono: 'red', vacio: 'No hay SCTR vencidos.' },
      { titulo: 'SCTR por vencer', items: alertasSsoma.sctr?.por_vencer, tono: 'amber', vacio: 'No hay SCTR por vencer.' },
      { titulo: 'EMO vencidos', items: alertasSsoma.emo?.vencidos, tono: 'red', vacio: 'No hay EMO vencidos.' },
      { titulo: 'EMO por vencer', items: alertasSsoma.emo?.por_vencer, tono: 'amber', vacio: 'No hay EMO por vencer.' },
      { titulo: 'EPP vencidos', items: alertasSsoma.epp?.vencidos, tono: 'red', vacio: 'No hay EPP vencidos.' },
      { titulo: 'EPP por vencer', items: alertasSsoma.epp?.por_vencer, tono: 'amber', vacio: 'No hay EPP por vencer.' },
      { titulo: 'Cursos vencidos', items: alertasSsoma.cursos?.vencidos, tono: 'red', vacio: 'No hay cursos vencidos.' },
      { titulo: 'Cursos por vencer', items: alertasSsoma.cursos?.por_vencer, tono: 'amber', vacio: 'No hay cursos por vencer.' },
    ];
    const tonoCaja = {
      red: 'border-red-200 bg-red-50 text-red-900',
      amber: 'border-amber-200 bg-amber-50 text-amber-900',
      rose: 'border-rose-200 bg-rose-50 text-rose-900',
      emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    };
    const tonoChip = {
      red: 'bg-red-100 text-red-900 border-red-200',
      amber: 'bg-amber-100 text-amber-900 border-amber-200',
    };

    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Resumen SSOMA</h2>
            <p className="text-sm text-gray-500">Documentos vencidos y por vencer. Entra a la ficha para regularizarlos.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={ssomaPath} className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700">
              Ir a fichas SSOMA
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {kpis.map((kpi) => (
            <div key={kpi.titulo} className={`rounded-2xl border p-4 ${tonoCaja[kpi.color]}`}>
              <p className="text-xs uppercase tracking-wide opacity-80">{kpi.titulo}</p>
              <p className="text-2xl font-bold">{loadingAlertasSsoma ? '…' : (kpi.valor ?? 0)}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {bloques.map((bloque) => (
            <div key={bloque.titulo} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">{bloque.titulo}</h3>
                <span className="text-xs font-bold text-gray-500">{(bloque.items || []).length}</span>
              </div>
              <div className="p-4">
                {loadingAlertasSsoma ? (
                  <p className="text-sm text-gray-500">Cargando…</p>
                ) : (bloque.items || []).length === 0 ? (
                  <p className="text-sm text-gray-500">{bloque.vacio}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(bloque.items || []).map((item) => (
                      <Link
                        key={`${bloque.titulo}-${item.registro_id || item.empleado_id}`}
                        to={pathWithEmpresa(`/ssoma?empleado=${item.empleado_id}`, empresaActivaId)}
                        className={`inline-flex flex-col px-3 py-2 rounded-lg border text-xs ${tonoChip[bloque.tono]}`}
                      >
                        <span className="font-semibold">{item.nombre_completo}</span>
                        <span>
                          {item.umbral === 'vencido' ? 'Venció' : 'Vence'} {formatearFecha(item.fecha_vencimiento)}
                          {item.detalle ? ` · ${item.detalle}` : ''}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {(alertasSsoma.accidentes_abiertos || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-rose-200 overflow-hidden">
            <div className="px-5 py-3 bg-rose-50 border-b border-rose-100">
              <h3 className="text-sm font-semibold text-rose-900">Accidentes abiertos</h3>
            </div>
            <div className="p-4 flex flex-wrap gap-2">
              {alertasSsoma.accidentes_abiertos.map((item) => (
                <Link
                  key={`acc-${item.id}`}
                  to={pathWithEmpresa(`/ssoma?accidente=${item.id}`, empresaActivaId)}
                  className="inline-flex flex-col px-3 py-2 rounded-lg bg-rose-100 text-rose-900 border border-rose-200 text-xs"
                >
                  <span className="font-semibold">{(item.empleados || []).join(', ') || 'Sin involucrados'}</span>
                  <span>{item.tipo} · {item.gravedad || '—'} · {formatearFecha(item.fecha_hora)}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const stats = [
    {
      title: 'Empleados Activos',
      value: loading ? '...' : totalEmpleados.toString(),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      accentColor: 'bg-blue-500',
      link: '/empleados'
    },
    {
      title: 'Planillas Cerradas',
      value: loading ? '...' : totalPlanillas.toString(),
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      accentColor: 'bg-green-500',
      link: '/planillas'
    }
  ];

  const quickActions = [
    {
      title: 'Agregar Nuevo Empleado',
      description: 'Registrar un nuevo empleado en el sistema',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      ),
      link: nuevoEmpleadoPath,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      enabled: true
    },
    {
      title: 'Ver Planillas',
      description: 'Acceder a la gestión de planillas',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      link: '/planillas',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      enabled: true
    },
    {
      title: 'Cálculos',
      description: 'Gratificaciones, CTS y otros cálculos',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      link: empresaActivaId ? pathWithEmpresa(`/calculos/${empresaActivaId}`, empresaActivaId) : '/calculos',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      enabled: true
    },
    {
      title: 'Reportes',
      description: 'Ver reportes y análisis del sistema',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      link: '/reportes',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      enabled: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className="group relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 hover:shadow-lg hover:shadow-gray-200/50 hover:border-gray-200 transition-all duration-300 overflow-hidden pl-8"
          >
            <div className={`absolute top-0 left-0 w-1.5 h-full rounded-l-2xl ${stat.accentColor}`} />
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                  {stat.title}
                </p>
                <p className="text-3xl md:text-4xl font-bold text-gray-900 tabular-nums">
                  {stat.value}
                </p>
              </div>
              <div className={`flex-shrink-0 w-14 h-14 ${stat.bgColor} rounded-2xl flex items-center justify-center ${stat.textColor} group-hover:scale-105 transition-transform duration-300`}>
                {stat.icon}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico: Ingresos totales por planilla (últimos meses) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Ingresos totales por planilla
            </h3>
            <p className="text-sm text-gray-500">
              Últimos 6 meses atrás (planillas cerradas)
            </p>
          </div>
          <div className="h-64">
            {loading ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">Cargando...</div>
            ) : ingresosPorMes.length === 0 ? (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">No hay planillas cerradas para mostrar</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ingresosPorMes} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="periodo" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}k` : v} />
                  <Tooltip formatter={(v) => `S/ ${Number(v).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`} labelStyle={{ color: '#374151' }} />
                  <Legend />
                  <Line type="monotone" dataKey="totalIngresos" name="Planilla" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="totalIngresosRH" name="RH" stroke="#a855f7" strokeWidth={2} dot={{ fill: '#a855f7', r: 4 }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Acciones Rápidas (compacto) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="mb-3">
            <h3 className="text-lg font-semibold text-gray-900">
              Acciones Rápidas
            </h3>
            <p className="text-xs text-gray-500">
              Accesos directos
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((action, index) => {
              if (action.enabled) {
                return (
                  <Link
                    key={index}
                    to={action.link}
                    className="flex items-center gap-2 p-3 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
                  >
                    <div className={`w-9 h-9 flex-shrink-0 ${action.bgColor} rounded-lg flex items-center justify-center ${action.color}`}>
                      {action.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xs font-medium text-gray-900 truncate">
                        {action.title}
                      </h4>
                      <p className="text-[10px] text-gray-500 truncate hidden sm:block">
                        {action.description}
                      </p>
                    </div>
                    <svg className="w-4 h-4 flex-shrink-0 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                );
              }
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 p-3 rounded-xl border border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                >
                  <div className={`w-9 h-9 flex-shrink-0 ${action.bgColor} rounded-lg flex items-center justify-center ${action.color}`}>
                    {action.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs font-medium text-gray-500 truncate">{action.title}</h4>
                    <p className="text-[10px] text-gray-400 truncate hidden sm:block">{action.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {contratosActivados && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-amber-200 overflow-hidden">
            <div className="px-5 py-4 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-amber-900">Contratos por vencer</h3>
                <p className="text-sm text-amber-700">
                  Personal con contratos ya firmados: solo aparecen si alguno vence en los próximos 30 días
                </p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-amber-500 text-white text-sm font-bold">
                {loadingAlertasContratos ? '…' : alertasContratos.totales?.por_vencer ?? 0}
              </span>
            </div>
            <div className="p-5">
              {loadingAlertasContratos ? (
                <p className="text-sm text-gray-500">Cargando alertas...</p>
              ) : alertasContratos.por_vencer?.length === 0 ? (
                <p className="text-sm text-gray-500">No hay personal con contratos por vencer.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {alertasContratos.por_vencer.map((item) => (
                    <span
                      key={`vencer-${item.empleado_id}-${item.contrato_id}`}
                      className="inline-flex items-center px-3 py-2 rounded-lg bg-amber-100 text-amber-900 border border-amber-200 text-xs font-semibold"
                      title={
                        [
                          item.titulo,
                          item.fecha_fin ? `Vence ${formatearFecha(item.fecha_fin)}` : null,
                          item.dias_restantes != null ? `${item.dias_restantes} día(s)` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')
                      }
                    >
                      {item.nombre_completo}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <Link to="/empleados" className="text-sm font-medium text-amber-700 hover:text-amber-900">
                  Ver empleados →
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-orange-200 overflow-hidden">
            <div className="px-5 py-4 bg-orange-50 border-b border-orange-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-orange-900">Personal pendiente de contrato</h3>
                <p className="text-sm text-orange-700">Personal activo sin contrato o con contrato pendiente de firma</p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-orange-500 text-white text-sm font-bold">
                {loadingAlertasContratos ? '…' : alertasContratos.totales?.pendientes ?? 0}
              </span>
            </div>
            <div className="p-5">
              {loadingAlertasContratos ? (
                <p className="text-sm text-gray-500">Cargando alertas...</p>
              ) : alertasContratos.pendientes?.length === 0 ? (
                <p className="text-sm text-gray-500">No hay personal pendiente de contrato.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {alertasContratos.pendientes.map((item) => (
                    <span
                      key={`pendiente-${item.empleado_id}-${item.tipo}-${item.contrato_id || 'sin'}`}
                      className="inline-flex items-center px-3 py-2 rounded-lg bg-orange-100 text-orange-900 border border-orange-200 text-xs font-semibold"
                      title={item.motivo || ''}
                    >
                      {item.nombre_completo}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <Link to="/empleados" className="text-sm font-medium text-orange-700 hover:text-orange-900">
                  Ver empleados →
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-red-200 overflow-hidden">
            <div className="px-5 py-4 bg-red-50 border-b border-red-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-red-900">Contratos vencidos</h3>
                <p className="text-sm text-red-700">
                  Personal activo con todos los contratos vencidos — clic en el nombre para renovar
                </p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-red-500 text-white text-sm font-bold">
                {loadingAlertasContratos ? '…' : alertasContratos.totales?.vencidos ?? 0}
              </span>
            </div>
            <div className="p-5">
              {loadingAlertasContratos ? (
                <p className="text-sm text-gray-500">Cargando alertas...</p>
              ) : alertasContratos.vencidos?.length === 0 ? (
                <p className="text-sm text-gray-500">No hay personal con todos los contratos vencidos.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {alertasContratos.vencidos.map((item) => {
                    const renovarPath = pathWithEmpresa(
                      `/empleados?renovarEmpleado=${item.empleado_id}` +
                        (item.contrato_id ? `&renovarContrato=${item.contrato_id}` : '') +
                        (item.plantilla_contrato_id
                          ? `&plantilla=${item.plantilla_contrato_id}`
                          : '') +
                        (item.plazo_meses != null ? `&plazo=${item.plazo_meses}` : '') +
                        (item.fecha_fin ? `&fechaFin=${item.fecha_fin}` : '') +
                        (item.titulo
                          ? `&titulo=${encodeURIComponent(item.titulo)}`
                          : '') +
                        (item.tipo_contrato
                          ? `&tipo=${encodeURIComponent(item.tipo_contrato)}`
                          : '') +
                        (item.lugar_firma
                          ? `&lugar=${encodeURIComponent(item.lugar_firma)}`
                          : ''),
                      empresaActivaId
                    );
                    return (
                      <Link
                        key={`vencido-${item.empleado_id}-${item.contrato_id || 'sin'}`}
                        to={renovarPath}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 text-red-900 border border-red-200 text-xs font-semibold hover:bg-red-200 hover:border-red-300 transition-colors"
                        title={
                          [
                            item.titulo,
                            item.fecha_fin ? `Venció ${formatearFecha(item.fecha_fin)}` : null,
                            'Clic para renovar',
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        }
                      >
                        <span>{item.nombre_completo}</span>
                        <span className="inline-flex px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide">
                          Renovar
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
              <div className="mt-4">
                <Link
                  to={empresaActivaId ? pathWithEmpresa('/empleados', empresaActivaId) : '/empleados'}
                  className="text-sm font-medium text-red-700 hover:text-red-900"
                >
                  Ver empleados →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {aniversariosActivados && (
        <div className="bg-white rounded-2xl shadow-sm border border-sky-200 overflow-hidden">
          <div className="px-5 py-4 bg-sky-50 border-b border-sky-100 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-sky-900">Aniversarios laborales</h3>
              <p className="text-sm text-sky-700">
                Personal activo próximo a cumplir{' '}
                {alertasAniversarios.anios != null ? `${alertasAniversarios.anios} año(s)` : 'la antigüedad configurada'}
                {alertasAniversarios.dias_anticipacion != null
                  ? ` (próximos ${alertasAniversarios.dias_anticipacion} días)`
                  : ''}
              </p>
            </div>
            <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-sky-500 text-white text-sm font-bold">
              {loadingAlertasAniversarios ? '…' : alertasAniversarios.total ?? 0}
            </span>
          </div>
          <div className="p-5">
            {loadingAlertasAniversarios ? (
              <p className="text-sm text-gray-500">Cargando aniversarios...</p>
            ) : (alertasAniversarios.empleados || []).length === 0 ? (
              <p className="text-sm text-gray-500">
                No hay trabajadores activos próximos a cumplir esa antigüedad.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {alertasAniversarios.empleados.map((item) => (
                  <span
                    key={`aniv-${item.id}`}
                    className="inline-flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-sky-100 text-sky-900 border border-sky-200 text-xs"
                    title={item.cargo || ''}
                  >
                    <span className="font-semibold">{item.nombre_completo}</span>
                    <span className="text-sky-800">
                      Cumple {formatearFecha(item.fecha_aniversario)}
                      {item.dias_restantes != null
                        ? item.dias_restantes === 0
                          ? ' · Hoy'
                          : ` · ${item.dias_restantes} día(s)`
                        : ''}
                    </span>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-4">
              <Link to="/configuracion" className="text-sm font-medium text-sky-700 hover:text-sky-900">
                Ir a configuración →
              </Link>
            </div>
          </div>
        </div>
      )}

      {ssomaActivado && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 overflow-hidden">
            <div className="px-5 py-4 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-emerald-900">SCTR vencido / por vencer</h3>
                <p className="text-sm text-emerald-700">
                  Recordatorios fijos: 1 semana, 3 días, 2 días y 1 hora antes. No es el concepto de planilla.
                </p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-emerald-600 text-white text-sm font-bold">
                {loadingAlertasSsoma
                  ? '…'
                  : (alertasSsoma.totales?.sctr_vencidos ?? 0) + (alertasSsoma.totales?.sctr_por_vencer ?? 0)}
              </span>
            </div>
            <div className="p-5 space-y-3">
              {loadingAlertasSsoma ? (
                <p className="text-sm text-gray-500">Cargando alertas...</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {(alertasSsoma.sctr?.vencidos || []).map((item) => (
                      <Link
                        key={`ssoma-sctr-v-${item.registro_id}`}
                        to={pathWithEmpresa('/ssoma', empresaActivaId)}
                        className="inline-flex flex-col px-3 py-2 rounded-lg bg-red-100 text-red-900 border border-red-200 text-xs"
                      >
                        <span className="font-semibold">{item.nombre_completo}</span>
                        <span>Venció {formatearFecha(item.fecha_vencimiento)}</span>
                      </Link>
                    ))}
                    {(alertasSsoma.sctr?.por_vencer || []).map((item) => (
                      <Link
                        key={`ssoma-sctr-p-${item.registro_id}`}
                        to={pathWithEmpresa('/ssoma', empresaActivaId)}
                        className="inline-flex flex-col px-3 py-2 rounded-lg bg-amber-100 text-amber-900 border border-amber-200 text-xs"
                      >
                        <span className="font-semibold">{item.nombre_completo}</span>
                        <span>Vence {formatearFecha(item.fecha_vencimiento)}</span>
                      </Link>
                    ))}
                  </div>
                  {(alertasSsoma.sctr?.vencidos || []).length === 0 &&
                    (alertasSsoma.sctr?.por_vencer || []).length === 0 && (
                      <p className="text-sm text-gray-500">No hay SCTR vencidos ni por vencer.</p>
                    )}
                </>
              )}
              <Link to={pathWithEmpresa('/ssoma', empresaActivaId)} className="text-sm font-medium text-emerald-700 hover:text-emerald-900">
                Ir a SSOMA →
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-teal-200 overflow-hidden">
            <div className="px-5 py-4 bg-teal-50 border-b border-teal-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-teal-900">EPP, cursos y accidentes</h3>
                <p className="text-sm text-teal-700">Alertas internas del módulo SSOMA</p>
              </div>
            </div>
            <div className="p-5 text-sm text-gray-700 space-y-2">
              <p>EMO vencido: <strong>{alertasSsoma.totales?.emo_vencidos ?? 0}</strong> · por vencer: <strong>{alertasSsoma.totales?.emo_por_vencer ?? 0}</strong></p>
              <p>EPP vencido: <strong>{alertasSsoma.totales?.epp_vencidos ?? 0}</strong> · por vencer: <strong>{alertasSsoma.totales?.epp_por_vencer ?? 0}</strong></p>
              <p>Cursos vencidos: <strong>{alertasSsoma.totales?.cursos_vencidos ?? 0}</strong> · por vencer: <strong>{alertasSsoma.totales?.cursos_por_vencer ?? 0}</strong></p>
              <p>Accidentes abiertos: <strong>{alertasSsoma.totales?.accidentes_abiertos ?? 0}</strong></p>
              <Link to={pathWithEmpresa('/ssoma', empresaActivaId)} className="inline-block text-sm font-medium text-teal-700 hover:text-teal-900">
                Ver fichas SSOMA →
              </Link>
            </div>
          </div>
        </div>
      )}

      {expedienteActivado && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-rose-200 overflow-hidden">
            <div className="px-5 py-4 bg-rose-50 border-b border-rose-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-rose-900">Antecedentes vencidos</h3>
                <p className="text-sm text-rose-700">
                  Constancia de antecedentes policiales vencida. Solicite la renovación.
                </p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-rose-500 text-white text-sm font-bold">
                {loadingAlertasExpediente ? '…' : alertasExpediente.totales?.vencidos ?? 0}
              </span>
            </div>
            <div className="p-5">
              {loadingAlertasExpediente ? (
                <p className="text-sm text-gray-500">Cargando alertas...</p>
              ) : (alertasExpediente.vencidos || []).length === 0 ? (
                <p className="text-sm text-gray-500">No hay antecedentes policiales vencidos.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {alertasExpediente.vencidos.map((item) => (
                    <Link
                      key={`exp-venc-${item.empleado_id}-${item.documento_id}`}
                      to={pathWithEmpresa(`/empleados/${item.empleado_id}/editar`, empresaActivaId)}
                      className="inline-flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-rose-100 text-rose-900 border border-rose-200 text-xs hover:bg-rose-200"
                      title={item.cargo || ''}
                    >
                      <span className="font-semibold">{item.nombre_completo}</span>
                      <span>
                        Venció {formatearFecha(item.fecha_vencimiento)}
                        {item.dias_para_vencer != null
                          ? ` · hace ${Math.abs(item.dias_para_vencer)} día(s)`
                          : ''}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-orange-200 overflow-hidden">
            <div className="px-5 py-4 bg-orange-50 border-b border-orange-100 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-orange-900">Antecedentes por vencer</h3>
                <p className="text-sm text-orange-700">
                  Vencen en los próximos {alertasExpediente.dias_alerta ?? 15} días. Solicite la siguiente constancia.
                </p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-8 px-3 rounded-full bg-orange-500 text-white text-sm font-bold">
                {loadingAlertasExpediente ? '…' : alertasExpediente.totales?.por_vencer ?? 0}
              </span>
            </div>
            <div className="p-5">
              {loadingAlertasExpediente ? (
                <p className="text-sm text-gray-500">Cargando alertas...</p>
              ) : (alertasExpediente.por_vencer || []).length === 0 ? (
                <p className="text-sm text-gray-500">No hay antecedentes policiales por vencer.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {alertasExpediente.por_vencer.map((item) => (
                    <Link
                      key={`exp-prox-${item.empleado_id}-${item.documento_id}`}
                      to={pathWithEmpresa(`/empleados/${item.empleado_id}/editar`, empresaActivaId)}
                      className="inline-flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-orange-100 text-orange-900 border border-orange-200 text-xs hover:bg-orange-200"
                      title={item.cargo || ''}
                    >
                      <span className="font-semibold">{item.nombre_completo}</span>
                      <span>
                        Vence {formatearFecha(item.fecha_vencimiento)}
                        {item.dias_para_vencer != null
                          ? item.dias_para_vencer === 0
                            ? ' · Hoy'
                            : ` · ${item.dias_para_vencer} día(s)`
                          : ''}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Dashboard; 